import type { ReactNode } from 'react';

/**
 * The SideNav's rules, kept out of the component so they can be tested without
 * rendering it: no JSX, no MUI, no router here.
 */

export interface NavItem {
  label: string;
  path?: string;
  icon: ReactNode;
  disabled?: boolean;
  comingSoon?: boolean;
  tooltip?: string;
  /** A live count shown beside the label (#717), read by the SideNav. */
  countKey?: NavCountKey;
}

/**
 * The live counts a nav row can carry: open feedbacks (#717), client imports to
 * handle (#748), Profile Cleaning issues (#753), and the profiles whose exits
 * and investments are to complete, on Exit & Investment Review (#762).
 */
export type NavCountKey =
  'openFeedbacks' | 'pendingClientImports' | 'cleaningIssues' | 'exitsInvestmentsToComplete';

/** What a count is of, for the badge's accessible label and tooltip ("3 open"). */
export const NAV_COUNT_WORDING: Record<NavCountKey, string> = {
  openFeedbacks: 'open',
  pendingClientImports: 'client imports to handle',
  cleaningIssues: 'issues',
  exitsInvestmentsToComplete: 'exits & investments to complete',
};

export interface NavSection {
  title?: string;
  items: NavItem[];
}

/**
 * Prefix match on a segment boundary, so `/admin/api` does not light up on
 * `/admin/apifoo`.
 */
const matchesPrefix = (path: string, pathname: string) =>
  pathname === path || pathname.startsWith(`${path}/`);

/**
 * Active detection: exact for client, prefix for admin so detail/edit
 * sub-routes keep their parent lit (`/admin/collection/:id` → "Collection").
 *
 * Most specific wins: when one nav item's path is a prefix of another's, only
 * the deepest match is active: `/admin/profiles/deactivated` lights up
 * "Deactivated Profiles" alone, not "Profiles" as well.
 */
export function isNavPathActive(
  path: string,
  { pathname, navPaths, prefix }: { pathname: string; navPaths: string[]; prefix: boolean },
): boolean {
  if (!prefix) return pathname === path;
  if (!matchesPrefix(path, pathname)) return false;
  return !navPaths.some((other) => other.length > path.length && matchesPrefix(other, pathname));
}

/**
 * What a row's count badge says: nothing at zero or before the count is known
 * (no badge rather than a misleading 0), the number up to 99, then `99+` so the
 * badge keeps its width in a 248px nav.
 */
export function navCountLabel(count: number | null | undefined): string | null {
  if (count === null || count === undefined || count <= 0) return null;
  return count > 99 ? '99+' : String(count);
}

const formatCount = (n: number) => n.toLocaleString('en-US');

/**
 * The line of the "Profile Cleaning" tooltip (#754): the issues the badge
 * counts, exact (no `99+` here) with a thousands separator. A figure not read
 * yet has no line; a 0 that was read has one. The "Exits & investments to
 * complete" queue it used to name too has its own row since #762.
 */
export function cleaningCountLines(issues: number | null | undefined): string[] {
  if (issues === null || issues === undefined) return [];
  return [`${formatCount(issues)} ${issues === 1 ? 'issue' : 'issues'} to clean`];
}

/**
 * The line of the "Exit & Investment Review" tooltip (#762): the queue's size,
 * exact (no `99+` here) with a thousands separator, in the singular for one.
 * Nothing at 0 nor before the figure is read, like its badge.
 */
export function toCompleteCountLines(toComplete: number | null | undefined): string[] {
  if (toComplete === null || toComplete === undefined || toComplete <= 0) return [];
  return [
    `${formatCount(toComplete)} ${toComplete === 1 ? 'exit & investment' : 'exits & investments'} to complete`,
  ];
}

/**
 * The rows whose tooltip gives their exact figure, one string per line, rather
 * than the badge's `99+` (#754, #762); `null` for the other rows.
 */
function exactCountLines(key: NavCountKey, count: number | null | undefined): string[] | null {
  if (key === 'cleaningIssues') return cleaningCountLines(count);
  if (key === 'exitsInvestmentsToComplete') return toCompleteCountLines(count);
  return null;
}

/**
 * What the tooltip of a row with an exact figure says, one string per line
 * (#754, #762), or `null` for a row without one (its tooltip is a plain
 * string). Expanded, the row's label is on screen: the tooltip is the figure
 * alone, and there is none when nothing was read or the figure is 0.
 * Collapsed, the tooltip is the row's only text: its label, then the figure
 * read so far.
 */
export function exactCountTooltipLines({
  key,
  label,
  count,
  collapsed,
}: {
  key: NavCountKey;
  label: string;
  count: number | null | undefined;
  collapsed: boolean;
}): string[] | null {
  const lines = exactCountLines(key, count);
  if (lines === null) return null;
  if (collapsed) return lines.length > 0 ? [`${label}:`, ...lines] : [label];
  return (count ?? 0) > 0 ? lines : [];
}

/**
 * The accessible name of a collapsed row with an exact figure (#754, #762):
 * its tooltip is markup, which MUI does not turn into a label, so the row says
 * it itself, like the other collapsed rows ("Collection (9 client imports to
 * handle)"): the label, then the figure read so far. `undefined` for a row
 * without an exact figure, which a string tooltip names.
 */
export function exactCountAccessibleLabel(
  key: NavCountKey,
  label: string,
  count: number | null | undefined,
): string | undefined {
  const lines = exactCountLines(key, count);
  if (lines === null) return undefined;
  return lines.length > 0 ? `${label} (${lines.join(', ')})` : label;
}

/**
 * The accessible label of a row's badge, expanded ("3 open"). The rows with an
 * exact figure say it as their tooltip does ("1,234 exits & investments to
 * complete", "1 exit & investment to complete", #762); the others say the
 * badge's text and what it counts ("99+ issues"). `null` without a badge.
 */
export function navCountText(key: NavCountKey, count: number | null | undefined): string | null {
  const badge = navCountLabel(count);
  if (badge === null) return null;
  if (key === 'exitsInvestmentsToComplete') return toCompleteCountLines(count).join(', ');
  return `${badge} ${NAV_COUNT_WORDING[key]}`;
}
